import React from 'react';
import { Button } from 'components/Button/Button';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { SignupStep, SignUpUserType } from 'features/signUp/types';
import { useSignUpFormValidation } from 'features/signUp/hooks/useSignUpFormValidation/useSignUpFormValidation';

interface SignUpSubmitButtonProps {
  currentStep: SignupStep;
  userType: SignUpUserType;
  isSubmitting: boolean;
  customButtonText?: string;
}

const getSubmitButtonText = (step: SignupStep, translation: (key: string) => string): string => {
  switch (step) {
    case 'AccessCodeMatched':
    case 'EligibilityVerified':
    case 'SSOSignUpSuccess':
    case 'ShortCutSSO':
      return translation('continue_button_title');
    case 'PolicyAcceptance':
      return translation('accept_all');
    case 'Welcome':
      return translation('general_finish');
    default:
      return translation('submit');
  }
};

export const SignUpSubmitButton = ({
  currentStep,
  userType,
  isSubmitting,
  customButtonText,
}: SignUpSubmitButtonProps) => {
  const { translation } = useTranslationContext();
  const { canSubmit } = useSignUpFormValidation(currentStep, userType);

  const buttonText = customButtonText || getSubmitButtonText(currentStep, translation);

  const isDisabled = !canSubmit || isSubmitting;

  // Don't render submit button for steps that don't need it
  const stepsWithoutSubmitButton: SignupStep[] = [
    'UserTypeSelection', // Has its own buttons
    'SSOSignupLoading',
  ];

  if (stepsWithoutSubmitButton.includes(currentStep)) {
    return null;
  }

  return (
    <Button
      variant="primary"
      type="submit"
      size="xlarge"
      className={`signup-submit-button signup-submit-button--${currentStep}`}
      disabled={isDisabled}
      isLoading={isSubmitting}
      fullWidth
    >
      {buttonText}
    </Button>
  );
};
